import type { UniqueIdentifier } from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMergeRefs } from '@floating-ui/react';
import type { CSSProperties, ReactElement, Ref } from 'react';
import { cloneElement } from 'react';

import { clstx } from '@payaca/utilities/cvaMerge';
import DndContext from '../dragAndDrop/DndContext';

import SortableActiveItemOverlay from './SortableActiveItemOverlay';
import useOptimisticReorder from './useOptimisticReorder';

interface BaseItem {
  id: UniqueIdentifier;
}

interface Props<T extends BaseItem> {
  items: T[];
  // Return `false` to reject the drop (e.g. failed validation); the rendered
  // order will snap back to `items`. Anything else (incl. void) commits.
  onDragEnd: (
    activeItem: T,
    destinationIndex: number,
    items: T[]
  ) => boolean | void;
  renderItem: (item: T) => ReactElement;
  renderDragOverlay?: (item: T) => ReactElement;
  id: string;
  isDisabled?: boolean;
  lockAxis?: boolean;
  className?: string;
  handle?: boolean;
  hasKeyboardSensor?: boolean;
}

interface SortableItemProps<T extends BaseItem> {
  item: T;
  renderItem: (item: T) => ReactElement;
  isDisabled?: boolean;
  handle?: boolean;
}

const HorizontalSortableList = <T extends BaseItem>(props: Props<T>) => {
  const {
    items,
    onDragEnd,
    renderItem,
    renderDragOverlay,
    id,
    isDisabled = false,
    lockAxis = false,
    className,
    handle = false,
    hasKeyboardSensor,
  } = props;

  const { setNodeRef } = useDroppable({ id });

  const { displayItems, commit } = useOptimisticReorder(items);

  return (
    <DndContext
      sortable
      hasKeyboardSensor={hasKeyboardSensor}
      modifiers={lockAxis ? [restrictToHorizontalAxis] : undefined}
      onDragEnd={({ active, over }) => {
        if (!over || active.id === over.id) return;
        const activeIndex = displayItems.findIndex(
          ({ id }) => id === active.id
        );
        const overIndex = displayItems.findIndex(({ id }) => id === over.id);
        const newItems = arrayMove(displayItems, activeIndex, overIndex);
        const result = onDragEnd(
          displayItems[activeIndex],
          overIndex,
          newItems
        );
        if (result !== false) commit(newItems);
      }}
    >
      <SortableContext
        items={displayItems}
        strategy={horizontalListSortingStrategy}
      >
        <div className={clstx('flex', className)} ref={setNodeRef}>
          {displayItems.map((item) => (
            <HorizontalSortableItem
              key={item.id}
              item={item}
              renderItem={renderItem}
              isDisabled={isDisabled}
              handle={handle}
            />
          ))}
        </div>
      </SortableContext>

      <SortableActiveItemOverlay
        items={displayItems}
        renderItem={renderDragOverlay ?? renderItem}
      />
    </DndContext>
  );
};

const HorizontalSortableItem = <T extends BaseItem>({
  item,
  renderItem,
  isDisabled,
  handle,
}: SortableItemProps<T>) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: isDisabled });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? undefined,
    opacity: isDragging ? 0 : undefined,
  };

  const child = renderItem(item);
  const childProps = child.props as {
    style?: CSSProperties;
    ref?: Ref<unknown>;
  };
  const mergedRef = useMergeRefs([setNodeRef, childProps.ref]);

  return cloneElement(
    child,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    {
      ref: mergedRef,
      style: { ...childProps.style, ...style },
      ...attributes,
      ...(!handle ? listeners : {}),
      ...(isDragging ? { 'data-dragging': true } : {}),
    } as any
  );
};

export default HorizontalSortableList;
